import { z } from 'zod';
import { specialtyCoverageItemSchema } from './specialty-coverage.schema';

/**
 * Custom validator for URLs and S3 file keys
 * 
 * Accepts:
 * - Full URLs: https://cdn.example.com/path/to/file.jpg
 * - S3 file keys: uploads/user123/file.jpg
 * - Legacy paths: providers/123/photo.jpg
 * - Legacy filenames: 1764022558918-photo.png
 * - Empty strings
 * 
 * Rejects:
 * - Invalid URLs
 * - Paths starting with / (absolute paths)
 * - Path traversal attempts (..)
 */
const urlOrFileKey = z
  .string()
  .refine(
    (val) => {
      // Allow empty values
      if (!val || val.trim() === '') return true;
      
      // Check if it's a full URL
      if (val.startsWith('http://') || val.startsWith('https://')) {
        try {
          new URL(val);
          return true;
        } catch {
          return false;
        }
      }
      
      // Reject absolute paths and path traversal
      if (val.startsWith('/') || val.includes('..')) {
        return false;
      }
      
      // Accept S3 file keys (with slashes) or legacy filenames (without slashes)
      // Valid: "uploads/user123/file.jpg", "1764022558918-photo.png", "photo.jpg"
      return true;
    },
    {
      message: 'Must be a valid URL or file path',
    }
  )
  .optional()
  .or(z.literal(''));

/**
 * User fields schema for provider editor
 * Note: email is required for create mode, read-only in edit mode
 */
const userSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email format'),
  firstName: z.string().min(1, 'First name is required').max(100, 'First name must be less than 100 characters'),
  lastName: z.string().max(100, 'Last name must be less than 100 characters').optional(),
  profilePhoto: urlOrFileKey,
  accountStatus: z.enum(['ACTIVE', 'INACTIVE', 'SUSPENDED', 'PENDING_VERIFICATION']).optional(),
});

/**
 * Provider fields schema for provider editor
 * Includes all 90+ fields from service_providers table
 */
const providerSchema = z.object({
  // Basic Information
  companyName: z.string().min(2, 'Company name must be at least 2 characters').max(200, 'Company name must be less than 200 characters'),
  businessAddress: z.string().max(500, 'Business address must be less than 500 characters').optional().or(z.literal('')),
  hideBusinessAddress: z.boolean().optional(), // Feature 057: Privacy control for address visibility
  latitude: z.number().min(-90, 'Latitude must be between -90 and 90').max(90, 'Latitude must be between -90 and 90').optional().nullable(),
  longitude: z.number().min(-180, 'Longitude must be between -180 and 180').max(180, 'Longitude must be between -180 and 180').optional().nullable(),
  // Feature 099 handoff (handoff-work-location-fields.md): structured location for Checkr work_locations.
  businessStateUspsCode: z.string().max(2).optional().nullable(),
  businessCountryIsoCode: z.string().max(2).optional().nullable(),

  // Status Fields
  vettingStatus: z.enum(['PENDING', 'IN_REVIEW', 'APPROVED', 'REJECTED']).optional(),
  newClientAcceptance: z.boolean().optional(),
  isBquestPreferred: z.boolean().optional(), // Feature 083: admin-set bQuest Preferred Provider
  
  // Contact Information
  phoneNumber: z.string().max(50, 'Phone number must be less than 50 characters').optional().or(z.literal('')),
  websiteUrl: z.string().url('Invalid URL format').optional().or(z.literal('')),
  calendlyUrl: z.string().url('Invalid URL format').optional().or(z.literal('')),
  
  // Service Information
  serviceOfferings: z.string().min(10, 'Service offerings must be at least 10 characters').max(5000, 'Service offerings must be less than 5000 characters').optional().or(z.literal('')),
  tagline: z.string().max(255, 'Tagline must be less than 255 characters').optional().or(z.literal('')),
  providerBio: z.string().optional().or(z.literal('')),
  companyOverview: z.string().optional().or(z.literal('')),
  
  // Pricing
  priceRangeIndicator: z.enum(['$', '$$', '$$$', '$$$$']).optional().nullable(),
  priceDetailsText: z.string().max(1570, 'Price details must be less than 1570 characters').optional().or(z.literal('')),
  
  // Media Fields
  heroMediaUrl: urlOrFileKey,
  mediaType: z.string().max(10, 'Media type must be less than 10 characters').optional().or(z.literal('')),
  introductionVideoUrl: urlOrFileKey,
  
  // Professional Information
  professionalTitle: z.string().max(255, 'Professional title must be less than 255 characters').optional().or(z.literal('')),
  yearsExperience: z.number().int('Years of experience must be an integer').min(0, 'Years of experience cannot be negative').optional().nullable(),
  yearsInBusiness: z.number().int('Years in business must be an integer').min(0, 'Years in business cannot be negative').optional().nullable(),
  establishedYear: z.number().int('Established year must be an integer').min(1800, 'Established year must be after 1800').optional().nullable(),
  
  // Arrays
  certificationsArray: z.array(z.string()).optional(),
  professionalLicenses: z.array(z.string()).optional(),
  awardsRecognitions: z.array(z.string()).optional(),
  languages: z.array(z.string()).optional(),
  professionalAssociations: z.array(z.string()).optional(),
  payerSources: z.array(z.string()).optional(),
  serviceRanges: z.array(z.enum(['IN_OFFICE', 'VIRTUAL', 'IN_HOME'])).optional(),
  
  // Additional Information
  targetAgeRange: z.string().max(100, 'Target age range must be less than 100 characters').optional().or(z.literal('')),
  carePhilosophy: z.string().optional().or(z.literal('')),
  whyThisWork: z.string().optional().or(z.literal('')),
  continuingEducation: z.string().optional().or(z.literal('')),
  
  // Emergency Services
  emergencyResponseAvailable: z.boolean().optional(),
  emergencyResponseDetails: z.string().optional().or(z.literal('')),
  
  // Hours and Operations
  hoursDescription: z.string().max(255, 'Hours description must be less than 255 characters').optional().or(z.literal('')),
  
  // Google Integration
  placeId: z.string().optional().or(z.literal('')),
  
  // Social Media (JSON field)
  socialHandles: z.record(z.string(), z.string()).optional().nullable(),

  // Admin-Only Fields (Feature 011 - US4: Admin Membership Tracking)
  membershipDate: z.date().optional().nullable(),
  renewalDate: z.date().optional().nullable(),
});

/**
 * Required URL or file key validator (for media gallery items)
 */
const requiredUrlOrFileKey = z
  .string()
  .min(1, 'Media URL is required')
  .refine(
    (val) => {
      // Check if it's a full URL
      if (val.startsWith('http://') || val.startsWith('https://')) {
        try {
          new URL(val);
          return true;
        } catch {
          return false;
        }
      }
      
      // Reject absolute paths and path traversal
      if (val.startsWith('/') || val.includes('..')) {
        return false;
      }
      
      // Accept S3 file keys or legacy filenames
      return true;
    },
    {
      message: 'Must be a valid URL or file path',
    }
  );

/**
 * Media gallery item schema for form state
 */
const mediaItemSchema = z.object({
  id: z.string(),
  mediaType: z.enum(['image', 'video']),
  mediaUrl: requiredUrlOrFileKey,
  caption: z.string().optional(),
  isPrimary: z.boolean(),
  displayOrder: z.number().int().min(0),
  isNew: z.boolean().optional(), // Flag for newly uploaded items
});

/**
 * Service catalog item schema for form state
 */
const serviceItemSchema = z.object({
  id: z.string().optional(), // Optional for new services
  serviceName: z.string().min(1, 'Service name is required').max(255, 'Service name must be less than 255 characters'),
  serviceDescription: z.string().max(500, 'Service description must be less than 500 characters').optional().or(z.literal('')),
  servicePrice: z.string().max(50, 'Service price must be less than 50 characters').optional().or(z.literal('')),
  priceType: z.string().max(50, 'Price type must be less than 50 characters').optional().or(z.literal('')),
  priceRangeIndicator: z.enum(['$', '$$', '$$$', '$$$$']).optional().nullable(),
  isActive: z.boolean().default(true),
  displayOrder: z.number().int().min(0).default(0),
  isNew: z.boolean().optional(), // Flag for newly created services
});

/**
 * Complete provider editor schema with user, provider, and relationship IDs
 */
export const providerEditorSchema = z.object({
  user: userSchema,
  provider: providerSchema,
  // Categories: Auto-selected from specialties (read-only in UI, but must have at least 1)
  categoryIds: z.array(z.string()).min(1, 'At least one specialty must be selected (categories are auto-selected from specialties)'),
  // Per-specialty coverage is the source of truth for specialty attachment (edit PATCH).
  specialties: z.array(specialtyCoverageItemSchema).min(1, 'At least one specialty is required'),
  expertiseIds: z.array(z.string()).optional().default([]),
  payerOptionIds: z.array(z.string()).optional().default([]),
  // Availability Schedule: Single selection (stored as single ID, not array)
  availabilityScheduleId: z.string().optional().or(z.literal('')),
  // Registered states (states where provider is licensed)
  registeredStateIds: z.array(z.string()).optional().default([]),
  // Media gallery items for preview sync
  mediaGallery: z.array(mediaItemSchema).optional().default([]),
  // Services catalog for form state
  servicesCatalog: z.array(serviceItemSchema).optional().default([]),
});

/**
 * Type inference for provider editor form data
 */
export type ProviderEditorFormData = z.infer<typeof providerEditorSchema>;

/**
 * Type for user section of the form
 */
export type UserFormData = z.infer<typeof userSchema>;

/**
 * Type for provider section of the form
 */
export type ProviderFormData = z.infer<typeof providerSchema>;

/**
 * Type for media gallery item
 */
export type MediaItemFormData = z.infer<typeof mediaItemSchema>;

/**
 * Type for service catalog item
 */
export type ServiceItemFormData = z.infer<typeof serviceItemSchema>;

